import { FC, PropsWithChildren, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setIsLoading, setUser } from "@store/authSlice";
import { Hub } from "@aws-amplify/core";
import { AuthService } from "@service/auth.service";
import { RootState } from "@store/store";
import { useSignOut } from "@hooks/useSignOut";

const authService = AuthService.instance;

const Auth: FC<PropsWithChildren<any>> = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isPatient = useSelector(
    (state: RootState) => state.auth.user?.isPatient,
  );
  const signOut = useSignOut();

  const getUser = useCallback(
    async (redirectToHome = false): Promise<void> => {
      try {
        dispatch(setIsLoading(true));
        const currentUser = await authService.loadCurrentUser();
        dispatch(setUser(currentUser));
        if (redirectToHome) {
          navigate("/");
        }
      } catch (error: any) {
        if (error.name !== "UserUnAuthenticatedException") {
          console.error(error);
        }
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, navigate],
  );

  useEffect(() => {
    if (isPatient) {
      return;
    }
    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          await getUser(true);
          break;
        case "signInWithRedirect":
          await getUser();
          break;
        case "signInWithRedirect_failure":
          console.error("Sign in with redirect failure, details: ", payload);
          break;
        case "tokenRefresh_failure":
          await signOut();
          break;
      }
    });

    getUser();

    return unsubscribe;
  }, [isPatient, getUser, dispatch, navigate, signOut]);

  return children;
};

export default Auth;
